import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch
} from '@invent/wl-ui-kit-next/ui';
import { Plus, X } from 'lucide-react';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/design';
import { ApiError } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import { firmListsApi } from '../api/firm-lists-api';
import type { IAdminVerifier } from '../api/participant-contracts';
import type { IAdminTeamFirm } from '../api/team-firm-contracts';
import { teamFirmsApi } from '../api/team-firms-api';
import { refusalMessage } from '../refusal';
import { type ISaidNote, NOT_REREAD, sentence, unanswered, unansweredNote } from './person-hub/said';

/**
 * What the Firms table (SR-513, Team routing) and its firm record share: a firm's names and the
 * writes that change them, and the one form that adds a firm.
 *
 * **A firm is a CRD; a register name is one of its names.** A register label mapped to the CRD
 * is an "other name" of the firm: choosing an existing label adds the CRD to that label's set,
 * typing a new one creates a label holding the CRD alone. survey-api's PATCH **replaces** a
 * label's set, so a change is the set read **afresh, just before**, plus or minus the one CRD —
 * never the set the page last drew, which after a write whose re-read failed is missing what that
 * write added. A fresh read that fails sends nothing, and says so (`NothingSent`).
 *
 * **Nothing here judges a firm.** Whether a CRD is a firm, a label taken or a name acceptable is
 * survey-api's to say, and its refusal is drawn by the member it names. The one thing held is a
 * CRD that is not digits: it is spliced into the wirehouse list's path.
 */

/**
 * The note a page or record says its writes by, and the two ways a write ends: landed, said once
 * `reread` has read the page again, or an answer that never arrived — `change` names what may have
 * changed.
 */
export function useWriteNote(reread: () => Promise<boolean>, change: string) {
  const [note, setNote] = React.useState<Nullable<ISaidNote>>(null);
  const landed = async (text: string) => {
    const read = await reread();
    setNote({ tone: 'done', text: text + (read ? '' : NOT_REREAD) });
  };
  const lost = async () => setNote(unansweredNote(await reread(), change));
  return { note, setNote, landed, lost };
}

/** A list member's or register name's last change, as a cell: when and by whom, or as seeded. */
export function changeStamp(at: string, by: Nullable<IAdminVerifier>): React.ReactNode {
  if (by === null) return 'As seeded';
  return (
    <>
      {formatDateTime(at)}
      <span className="block break-all">by {by.email}</span>
    </>
  );
}

/** The firm's CRD as typed — a positive whole number, or `null` where it is not one. */
export function crdOf(text: string): Nullable<number> {
  const trimmed = text.trim();
  if (!/^[0-9]+$/.test(trimmed)) return null;
  const id = Number(trimmed);
  return id > 0 && Number.isSafeInteger(id) ? id : null;
}

/** Said, and nothing sent, where the CRD box holds anything but digits. */
export const CRD_DIGITS = 'Type the firm’s CRD, digits only.';

/** What survey-api's 400 on `label` means where a firm goes on Wires with no name typed. */
const UNNAMED = 'Type a label: no BrokerCheck or IAPD answer has named this firm yet.';

/** Rankings' Other/RIA — the register's catch-all for every firm off it, not a firm: it is never
 *  offered as a firm's name, and survey-api refuses it a CRD (SR-513). */
export const OTHER_RIA = 'other_ria';

/** The wirehouse list — survey-api's `FirmLists.All` — the one list the Wires switch edits. */
export const WIREHOUSE = 'wirehouse';

/** A write that was never sent — its fresh read failed, or (`gone`) found the name gone. */
class NothingSent extends Error {
  constructor(
    message: string,
    readonly gone = false
  ) {
    super(message);
  }
}

/** Whether a refused write found its register name gone — survey-api's 404, or a fresh read that
 *  no longer held it: what the page drew is out of date, so the caller reads it again. */
export function isGone(error: unknown): boolean {
  return (error instanceof ApiError && error.status === 404) || (error instanceof NothingSent && error.gone);
}

/** Where survey-api's refusal of a name write belongs: by the member it names. */
interface IFirmRefusal {
  label?: string;
  finraFirmIds?: string;
  aliases?: string;
  /** A refusal naming anything else, or nothing. */
  form?: string;
}

/**
 * survey-api's refusal, sorted by the member each problem names; one with no `problems[]` is said
 * whole. Its binder names a member by JSON path (`$.finraFirmIds[0]`), which is still that
 * member's refusal, said with the piece it names. A spelling's (`aliases[i]`) is said of "the
 * spelling" — the one sent, since a write sends one — and the set's own of the name's spellings.
 */
function firmRefusal(error: unknown, fallback: string): IFirmRefusal {
  const problems = error instanceof ApiError ? (error.problem?.problems ?? []) : [];
  if (problems.length === 0) return { form: refusalMessage(error, fallback) };
  const said: IFirmRefusal = {};
  for (const { target, reason } of problems) {
    const path = target.replace(/^\$\./, '');
    const member = /^[A-Za-z]+/.exec(path)?.[0] ?? path;
    const key: keyof IFirmRefusal =
      member === 'label' || member === 'finraFirmIds' || member === 'aliases' ? member : 'form';
    const text =
      key === 'form'
        ? `${target}: ${reason}`
        : key === 'aliases'
          ? path === member
            ? sentence(spellingsReason(reason))
            : sentence(`The spelling ${reason}`)
          : path === member
            ? sentence(`${MEMBER_WORDS[key]} ${reason}`)
            : sentence(`${MEMBER_WORDS[key]}: ${path} — ${reason}`);
    said[key] = said[key] ? `${said[key]} ${text}` : text;
  }
  return said;
}

const MEMBER_WORDS: Record<keyof IFirmRefusal, string> = {
  label: 'The name',
  finraFirmIds: 'The CRD',
  aliases: 'The spelling',
  form: ''
};

/** survey-api's refusal of a name's whole set of spellings, said of the name. */
function spellingsReason(reason: string): string {
  const most = /^must be at most (\d+) spellings$/.exec(reason);
  return most ? `A register name takes at most ${most[1]} spellings` : `The spellings ${reason}`;
}

/** A refusal as one sentence — every member's part in turn. */
export function refusalText(error: unknown, fallback: string): string {
  if (error instanceof NothingSent) return error.message;
  if (error instanceof ApiError && error.status === 404) {
    return 'The register no longer holds that name.';
  }
  const refused = firmRefusal(error, fallback);
  return [refused.label, refused.finraFirmIds, refused.aliases, refused.form]
    .filter(Boolean)
    .join(' ');
}

/** Whether survey-api refused `error`'s request on the member `target`. */
export function refusedOn(error: unknown, target: string): boolean {
  return (
    error instanceof ApiError &&
    (error.problem?.problems ?? []).some((problem) => problem.target === target)
  );
}

/** Whether a failed write may have landed: an answer that never arrived. `NothingSent` did not. */
export function mayHaveLanded(error: unknown): boolean {
  return !(error instanceof NothingSent) && unanswered(error);
}

/** A register name, by its code and the label it is said by. */
interface IRegisterName {
  code: string;
  label: string;
}

/** A register name as survey-api holds it now — read afresh, since its set is replaced whole. */
async function freshName({ code, label }: IRegisterName): Promise<IAdminTeamFirm> {
  let register: IAdminTeamFirm[];
  try {
    register = await teamFirmsApi.list();
  } catch (error) {
    throw new NothingSent(
      'Could not read the register, so nothing was sent — ' +
        `${refusalMessage(error, 'the request failed').replace(/\.$/, '')}. Try again.`
    );
  }
  const found = register.find((name) => name.code === code);
  if (found === undefined) {
    throw new NothingSent(`The register no longer holds “${label}”, so nothing was sent.`, true);
  }
  return found;
}

/** A register name made one of the firm `crd`'s names: its set as held now, plus `crd`. */
export async function attachName(name: IRegisterName, crd: number): Promise<IAdminTeamFirm> {
  const now = await freshName(name);
  if (now.finraFirmIds.includes(crd)) return now;
  return teamFirmsApi.update(name.code, { finraFirmIds: [...now.finraFirmIds, crd] });
}

/** A register name taken off the firm `crd`: its set as held now, less `crd`. */
export async function detachName(name: IRegisterName, crd: number): Promise<IAdminTeamFirm> {
  const now = await freshName(name);
  return teamFirmsApi.update(name.code, {
    finraFirmIds: now.finraFirmIds.filter((id) => id !== crd)
  });
}

/** A spelling added to a register name: its spellings as held now, plus `spelling`. */
export async function addSpelling(name: IRegisterName, spelling: string): Promise<IAdminTeamFirm> {
  const now = await freshName(name);
  return teamFirmsApi.update(name.code, { aliases: [...now.aliases, spelling] });
}

/** A spelling taken off a register name: its spellings as held now, less `spelling`. */
export async function removeSpelling(
  name: IRegisterName,
  spelling: string
): Promise<IAdminTeamFirm> {
  const now = await freshName(name);
  return teamFirmsApi.update(name.code, {
    aliases: now.aliases.filter((alias) => alias.toLowerCase() !== spelling.toLowerCase())
  });
}

/**
 * A spelling's refusal. survey-api's 409 on `aliases` is a spelling another register name holds
 * as its label or spelling; its reason names the spelling, so the register — read again — says
 * whose it is. Any other refusal is said as it came.
 */
export async function spellingRefusal(error: unknown, spelling: string): Promise<string> {
  if (!(error instanceof ApiError && error.status === 409 && refusedOn(error, 'aliases'))) {
    return refusalText(error, 'Could not add this spelling.');
  }
  const needle = spelling.trim().toLowerCase();
  const holder = await teamFirmsApi.list().then(
    (register) =>
      register.find((name) =>
        [name.label, ...name.aliases].some((text) => text.toLowerCase() === needle)
      ),
    () => undefined
  );
  if (holder === undefined) return refusalText(error, 'Could not add this spelling.');
  const of =
    holder.finraFirmIds.length === 0
      ? 'a register name with no CRD'
      : `the register name of CRD ${holder.finraFirmIds.join(', ')}`;
  return holder.label.toLowerCase() === needle
    ? `“${spelling.trim()}” is already ${of}.`
    : `“${spelling.trim()}” is already a spelling of “${holder.label}”, ${of}.`;
}

/**
 * A name for a firm as it is written: a register name mapped to the CRD, a spelling of one of the
 * firm's register names, or a register name of its own (`new`) — the firm's first, or one chosen
 * as separate beside the others, which Rankings counts as a firm of its own.
 */
export type NameChoice =
  | { kind: 'existing'; code: string; label: string }
  | { kind: 'spelling'; label: string; code: string; of: string }
  | { kind: 'new'; label: string };

/**
 * What the picker hands back. A typed name is `new` for a firm with no register name — its first —
 * and `typed` for one with any: a spelling of one of them, chosen next (`SpellingTarget`), or,
 * `separate`, a register name of its own.
 */
export type NamePick =
  | Exclude<NameChoice, { kind: 'spelling' }>
  | { kind: 'typed'; label: string; separate: boolean };

/** A typed name's choice that it is no spelling, but a register name of its own. */
export const SEPARATE = '@separate';

/** Said before a separate register name is sent — Rankings groups a firm by its register name. */
export const SEPARATE_WARNING = 'Rankings treats this as a separate firm.';

/**
 * Said before every write that maps a CRD to a register name or changes a name's spellings: a team
 * whose profile names the firm, and whose lead BrokerCheck has not checked, is routed by that name.
 * Words only — the screen does not work out which teams.
 */
export const ROUTE_CHANGE_WARNING =
  'Teams whose profile names this firm and that BrokerCheck has not checked may change route now.';

/** The route-change sentence, drawn beside a confirmation or a box before it is sent. */
export function RouteChangeLine() {
  return (
    <p data-route-change="" className="text-sm text-muted-foreground">
      {ROUTE_CHANGE_WARNING}
    </p>
  );
}

/** Why a typed name's press waits, where the firm has several register names it could spell. */
export const CHOOSE_TARGET = 'Choose which name this spells.';

/** A firm's register name a typed name can be a spelling of. */
export interface ISpellingTarget {
  code: string;
  label: string;
}

/** Lowercase alphanumeric words — what `similarity` compares. */
function wordsOf(text: string): string[] {
  return text.toLowerCase().match(/[a-z0-9]+/g) ?? [];
}

/**
 * How close a register name is to a typed name — a small rule, any case and punctuation. A typed
 * word matches where one of the name's words starts with it or, two letters or more, where it is
 * the initials of consecutive words of the name ("ms" — Morgan Stanley); each of the name's words
 * is used once. Closer is more typed words matched, then fewer of the name's words left over.
 */
function similarity(typed: string, candidate: string): { matched: number; left: number } {
  const theirs = wordsOf(candidate);
  const used = new Set<number>();
  const free = (index: number) => !used.has(index);
  let matched = 0;
  for (const word of wordsOf(typed)) {
    const prefix = theirs.findIndex((one, index) => free(index) && one.startsWith(word));
    if (prefix >= 0) {
      used.add(prefix);
      matched += 1;
      continue;
    }
    if (word.length < 2) continue;
    for (let start = 0; start + word.length <= theirs.length; start += 1) {
      const run = Array.from({ length: word.length }, (_, offset) => start + offset);
      if (run.every(free) && run.map((index) => theirs[index][0]).join('') === word) {
        run.forEach((index) => used.add(index));
        matched += 1;
        break;
      }
    }
  }
  return { matched, left: theirs.length - used.size };
}

/** Names in order of closeness to a typed name (`similarity`), then alphabetically. */
function bySimilarity<T extends { label: string }>(typed: string, names: readonly T[]): T[] {
  return names
    .map((name) => ({ name, score: similarity(typed, name.label) }))
    .sort(
      (left, right) =>
        right.score.matched - left.score.matched ||
        left.score.left - right.score.left ||
        left.name.label.localeCompare(right.name.label)
    )
    .map(({ name }) => name);
}

/**
 * Which of the firm's register names a typed name spells, or a separate register name — the
 * names in order of closeness to it. Nothing is chosen for the reader where there are several;
 * the one where there is one, unless it was asked for as separate.
 */
export function SpellingTarget({
  typed,
  names,
  value,
  onChange,
  disabled
}: {
  typed: string;
  names: readonly ISpellingTarget[];
  value: Nullable<string>;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const group = React.useId();
  const option = (code: string, text: string) => (
    <label key={code} className="flex items-center gap-2 text-sm">
      <input
        type="radio"
        name={group}
        className="accent-[var(--color-sapphire)]"
        checked={value === code}
        onChange={() => onChange(code)}
        disabled={disabled}
      />
      {text}
    </label>
  );
  return (
    <fieldset className="grid gap-1.5" data-spelling-target="">
      <legend className="mb-1 text-sm">Which name does “{typed}” spell?</legend>
      {bySimilarity(typed, names).map((name) => option(name.code, name.label))}
      {option(SEPARATE, 'None — a separate register name')}
    </fieldset>
  );
}

/** What a typed name's choice starts as: the one name where there is one, else nothing. */
export function targetOf(pick: { separate: boolean }, names: readonly ISpellingTarget[]): Nullable<string> {
  if (pick.separate) return SEPARATE;
  return names.length === 1 ? names[0].code : null;
}

/** The write a choice makes for the firm `crd`, and what it is said as once it landed. */
export async function addName(choice: NameChoice, crd: number): Promise<string> {
  if (choice.kind === 'existing') {
    await attachName(choice, crd);
    return `“${choice.label}” now names CRD ${crd}.`;
  }
  if (choice.kind === 'spelling') {
    await addSpelling({ code: choice.code, label: choice.of }, choice.label);
    return `“${choice.label}” is now a spelling of “${choice.of}”.`;
  }
  await teamFirmsApi.create({ label: choice.label, finraFirmIds: [crd] });
  return `“${choice.label}” is a new name of CRD ${crd}.`;
}

/** The firm CRDs a register name holds, said briefly — a choice's line in the picker. */
function crdsLine(name: IAdminTeamFirm): string {
  return name.finraFirmIds.length === 0
    ? 'No CRD'
    : `CRD ${name.finraFirmIds.slice(0, 3).join(', ')}${name.finraFirmIds.length > 3 ? ', …' : ''}`;
}

/**
 * A name for a firm, picked from the register's names — searched by label and spelling — or typed.
 * A typed name the register holds as a label or spelling is offered as that name, never as a new
 * one (survey-api would refuse it as taken).
 *
 * **A firm with a register name takes a typed name as a spelling** (`named`): a spelling joins
 * the same Rankings firm, a new register name is another. So it is offered as a spelling first —
 * of the one name, or of the one the reader chooses next among several — and as a separate register
 * name only after it, by its own words. A firm with none takes it as a new name, its first.
 */
export function NamePicker({
  label,
  trigger,
  register,
  taken,
  named = [],
  allowNew = true,
  disabled,
  describedBy,
  onPick
}: {
  /** The control's accessible name. */
  label: string;
  /** What the button says. */
  trigger: React.ReactNode;
  register: readonly IAdminTeamFirm[];
  /** Codes not offered — the names the firm holds, or the form holds already. */
  taken: ReadonlySet<string>;
  /** The firm's register names a typed name could be a spelling of. */
  named?: readonly ISpellingTarget[];
  allowNew?: boolean;
  disabled?: boolean;
  describedBy?: string;
  onPick: (choice: NamePick) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const typed = query.trim();
  const needle = typed.toLowerCase();
  const spellings = (name: IAdminTeamFirm) => [name.label, ...name.aliases];
  const held = register.some((name) =>
    spellings(name).some((text) => text.toLowerCase() === needle)
  );
  // Filtered here, not by cmdk: its fuzzy score offers "Oppenheimer" for "Mer", and it hides the
  // New group's one item with the rest when no register name matches.
  const offered = register.filter(
    (name) =>
      name.code !== OTHER_RIA &&
      !taken.has(name.code) &&
      spellings(name).some((text) => text.toLowerCase().includes(needle))
  );
  const offerNew = allowNew && typed !== '' && !held;
  const pick = (choice: NamePick) => {
    onPick(choice);
    setOpen(false);
    setQuery('');
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* The trigger's own `data-slot` would replace the button's, and with it the house's
            button type (`global.css`). */}
        <Button
          data-slot="button"
          type="button"
          size="sm"
          variant="outline"
          aria-label={label}
          aria-describedby={describedBy}
          disabled={disabled}
          className="w-fit"
        >
          <Plus className="size-4" aria-hidden />
          {trigger}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(26rem,calc(100vw-2rem))] p-0">
        <Command label={label} shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={allowNew ? 'A name on the register, or a new one' : 'A name on the register'}
          />
          <CommandList className="max-h-[min(20rem,50dvh)] py-1">
            {offered.length === 0 && !offerNew && (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                No name on the register matches.
              </p>
            )}
            {offerNew && named.length > 0 && (
              <CommandGroup heading="Typed">
                <CommandItem
                  value={`spelling:${typed}`}
                  onSelect={() => pick({ kind: 'typed', label: typed, separate: false })}
                >
                  {named.length === 1
                    ? `Add “${typed}” as a spelling of “${named[0].label}”`
                    : `Add “${typed}” as a spelling of one of its names`}
                </CommandItem>
                <CommandItem
                  value={`separate:${typed}`}
                  onSelect={() => pick({ kind: 'typed', label: typed, separate: true })}
                >
                  Add “{typed}” as a separate register name
                </CommandItem>
              </CommandGroup>
            )}
            {offerNew && named.length === 0 && (
              <CommandGroup heading="New">
                <CommandItem
                  value={`new:${typed}`}
                  onSelect={() => pick({ kind: 'new', label: typed })}
                >
                  Add “{typed}” as a new name
                </CommandItem>
              </CommandGroup>
            )}
            {offered.length > 0 && (
              <CommandGroup heading="On the register">
                {offered.map((name) => (
                  <CommandItem
                    key={name.code}
                    value={`name:${name.code}`}
                    onSelect={() => pick({ kind: 'existing', code: name.code, label: name.label })}
                  >
                    <span className="flex min-w-0 flex-1 items-baseline justify-between gap-3">
                      <span className="truncate">{name.label}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{crdsLine(name)}</span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** What a step of "Add a firm" was told. */
interface IStepSaid {
  tone: 'done' | 'refused';
  text: string;
}

/**
 * "Add a firm" — one form for a firm by CRD: its name, whether it is on Wires, and any other
 * names, existing or typed. **No route field**: the wirehouse list routes, and the register's
 * stored route is never sent.
 *
 * The writes, in order: on Wires the member's `PUT` with the name as its label (none typed takes
 * the registry's spelling, and a firm nobody named yet is refused for it — `UNNAMED`); off Wires a
 * register name holding the CRD, so the firm exists by name; then each other name (`planOf`) — an
 * existing one gains the CRD; a typed one is the firm's first register name where it has none yet
 * (on Wires, at a CRD the register does not name), and **a spelling of one of its register names
 * after that** — the one there is, or the one the reader chooses where there are several (the
 * press waits, saying why) — unless it was chosen as a separate register name, which the form
 * warns of. **They are not one transaction**: a refused first write sends nothing more, a refused
 * other name does not stop the next — but a spelling whose register name was refused waits for
 * it — and the dialog says what landed and what was refused, by name, keeping only what is left to
 * send. An answer that never arrived closes the form and re-reads (`onLost`), since a retry of
 * what landed would be refused as taken.
 *
 * **A CRD the register already names is a firm already**: the form makes nothing for it — another
 * register name there would be another Rankings firm — and offers its record instead (`onOpenFirm`).
 * It knows by the register the page read, and asks again just before the first write, in case that
 * read is behind; a fresh read that fails does not hold the form back.
 */
export function AddFirmDialog({
  register,
  initialCrd = '',
  initialName = '',
  onClose,
  onOpenFirm,
  onLanded,
  onLost
}: {
  register: readonly IAdminTeamFirm[];
  initialCrd?: string;
  initialName?: string;
  onClose: () => void;
  /** The record of the firm a CRD typed already is — the form closes for it. */
  onOpenFirm: (crd: number) => void;
  /** Everything landed — what to say on the page — or the dialog stays to say what did not. */
  onLanded: (said: string, closed: boolean) => void;
  onLost: () => void;
}) {
  const [crd, setCrd] = React.useState(initialCrd);
  const [name, setName] = React.useState(initialName);
  const [wires, setWires] = React.useState(false);
  const [others, setOthers] = React.useState<IFormName[]>([]);
  const [busy, setBusy] = React.useState(false);
  // The first write landed: the firm exists, and its CRD, name and route are no longer the form's.
  const [made, setMade] = React.useState(false);
  const [held, setHeld] = React.useState<Nullable<string>>(null);
  const [refused, setRefused] = React.useState<IFirmRefusal>({});
  const [said, setSaid] = React.useState<IStepSaid[]>([]);
  const crdId = React.useId();
  const nameId = React.useId();
  const wiresId = React.useId();
  const nameRefusedId = React.useId();
  const waitId = React.useId();
  const namedId = React.useId();
  // A CRD the fresh read before the first write found named, where the page's read was behind.
  const [foundNamed, setFoundNamed] = React.useState<Nullable<number>>(null);

  // The firm's first register name the form made, once it landed.
  const [anchor, setAnchor] = React.useState<Nullable<ISpellingTarget>>(null);

  const id = crdOf(crd);
  const mapped = new Set(others.flatMap((other) => (other.kind === 'existing' ? [other.code] : [])));
  // The register names the CRD has already — a typed name may spell one of them too — but the one
  // this form made, which a re-read after a partial failure holds too: it is `FIRST` below.
  const already = (id === null ? [] : register).filter(
    (one) =>
      one.code !== OTHER_RIA &&
      one.code !== anchor?.code &&
      one.finraFirmIds.includes(id ?? -1) &&
      !mapped.has(one.code)
  );
  const start: ISpellingTarget[] = [
    ...(anchor !== null || !wires
      ? [{ code: FIRST, label: anchor?.label ?? (name.trim() || 'the firm’s name') }]
      : []),
    ...already.map(({ code, label }) => ({ code, label }))
  ];
  const plan = planOf(others, start);
  const waiting = plan.steps.findIndex((step) => step.role === 'choose');
  const holdsCrd = (names: readonly IAdminTeamFirm[], crdId: number) =>
    names.some((one) => one.code !== OTHER_RIA && one.finraFirmIds.includes(crdId));
  const named = !made && id !== null && (foundNamed === id || holdsCrd(register, id));

  const submit = async () => {
    if (id === null) return setHeld(CRD_DIGITS);
    if (!wires && name.trim() === '') return setHeld('Type the firm’s name.');
    if (waiting >= 0 || named) return;
    setHeld(null);
    setRefused({});
    setBusy(true);
    const told: IStepSaid[] = [];
    try {
      let first = anchor;
      if (!made) {
        const fresh = await teamFirmsApi.list().catch(() => null);
        if (fresh !== null && holdsCrd(fresh, id)) {
          setFoundNamed(id);
          return;
        }
        const label = name.trim();
        try {
          if (wires) {
            const member = await firmListsApi.addMember(WIREHOUSE, id, label === '' ? undefined : label);
            told.push({ tone: 'done', text: `${member.label} (CRD ${id}) is on Wires.` });
          } else {
            const created = await teamFirmsApi.create({ label, finraFirmIds: [id] });
            first = { code: created.code, label: created.label };
            setAnchor(first);
            told.push({ tone: 'done', text: `${label} (CRD ${id}) is a firm on Gating.` });
          }
          setMade(true);
        } catch (error) {
          if (mayHaveLanded(error)) return onLost();
          setRefused(
            wires && label === '' && refusedOn(error, 'label')
              ? { label: UNNAMED }
              : firmRefusal(error, 'Could not add this firm.')
          );
          setSaid([]);
          return;
        }
      }
      const left: IFormName[] = [];
      let firstRefused: Nullable<string> = null;
      for (const [index, other] of others.entries()) {
        const step = plan.steps[index];
        // A spelling of the form's first register name, where that was refused, waits for it.
        if (step.target?.code === FIRST && first === null) {
          left.push(other);
          told.push({
            tone: 'refused',
            text: `“${other.label}” was not sent: it waits for “${firstRefused ?? step.target.label}”.`
          });
          continue;
        }
        try {
          if (other.kind === 'existing') {
            told.push({ tone: 'done', text: await addName(other, id) });
          } else if (step.role === 'spelling' && step.target) {
            const target = step.target.code === FIRST && first !== null ? first : step.target;
            told.push({
              tone: 'done',
              text: await addName(
                { kind: 'spelling', label: other.label, code: target.code, of: target.label },
                id
              )
            });
          } else {
            const created = await teamFirmsApi.create({ label: other.label, finraFirmIds: [id] });
            if (step.role === 'first') {
              first = { code: created.code, label: created.label };
              setAnchor(first);
            }
            told.push({ tone: 'done', text: `“${other.label}” is a new name of CRD ${id}.` });
          }
        } catch (error) {
          if (mayHaveLanded(error)) return onLost();
          if (step.role === 'first') firstRefused = other.label;
          left.push(other);
          told.push({
            tone: 'refused',
            text:
              `“${other.label}” was not added: ` +
              (step.role === 'spelling'
                ? await spellingRefusal(error, other.label)
                : refusalText(error, 'refused.'))
          });
        }
      }
      setOthers(left);
      const summary = told.filter((one) => one.tone === 'done').map((one) => one.text).join(' ');
      if (left.length === 0) onLanded(summary, true);
      else {
        setSaid(told);
        if (summary !== '') onLanded(summary, false);
      }
    } finally {
      setBusy(false);
    }
  };

  /** A typed name's choice of what it spells. */
  const choose = (other: IFormName, target: string) =>
    setOthers((now) => now.map((one) => (one === other ? { ...one, target } : one)));

  return (
    <Dialog open onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add a firm</DialogTitle>
          <DialogDescription>A firm by its CRD, with the names it is known by.</DialogDescription>
        </DialogHeader>
        <DialogBody
          as="form"
          id={`${crdId}-form`}
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor={crdId}>CRD</Label>
            <Input
              id={crdId}
              inputMode="numeric"
              autoComplete="off"
              value={crd}
              onChange={(event) => setCrd(event.target.value)}
              disabled={busy || made}
              aria-invalid={refused.finraFirmIds ? true : undefined}
              aria-describedby={named ? namedId : undefined}
              autoFocus
            />
            {refused.finraFirmIds && (
              <p className="text-sm text-destructive">{refused.finraFirmIds}</p>
            )}
            {named && id !== null && (
              <p id={namedId} data-crd-named="" className="text-sm">
                This CRD is already a firm —{' '}
                <button
                  type="button"
                  className="font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
                  onClick={() => onOpenFirm(id)}
                >
                  open it
                </button>
              </p>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={nameId}>Name</Label>
            <Input
              id={nameId}
              autoComplete="off"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={busy || made}
              aria-invalid={refused.label ? true : undefined}
              aria-describedby={refused.label ? nameRefusedId : undefined}
            />
            {refused.label && (
              <p id={nameRefusedId} className="text-sm text-destructive">
                {refused.label}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id={wiresId}
              checked={wires}
              onCheckedChange={setWires}
              disabled={busy || made}
            />
            <Label htmlFor={wiresId}>Wires — on the wirehouse list</Label>
          </div>
          <fieldset className="grid gap-2">
            <legend className="mb-1.5 text-sm font-medium">Other names</legend>
            {others.length > 0 && (
              <ul className="flex flex-wrap gap-1.5" aria-label="Other names to add">
                {others.map((other, index) => (
                  <li key={`${other.kind}:${other.label}`}>
                    <NameChip
                      text={other.label + roleWords(plan.steps[index])}
                      remove={`Leave out “${other.label}”`}
                      disabled={busy}
                      onRemove={() => setOthers((now) => now.filter((one) => one !== other))}
                    />
                  </li>
                ))}
              </ul>
            )}
            {others.map((other, index) => {
              const step = plan.steps[index];
              // Where the name could spell one of several register names, the reader says which.
              if (other.kind === 'existing' || step.choices.length < 2) return null;
              return (
                <SpellingTarget
                  key={`target:${other.label}`}
                  typed={other.label}
                  names={step.choices}
                  value={step.role === 'separate' ? SEPARATE : (step.target?.code ?? null)}
                  onChange={(target) => choose(other, target)}
                  disabled={busy}
                />
              );
            })}
            <NamePicker
              label="Add an other name"
              trigger="Add an other name"
              register={register}
              taken={mapped}
              named={plan.targets}
              disabled={busy}
              onPick={(pick) =>
                setOthers((now) =>
                  now.some((one) => one.label.toLowerCase() === pick.label.toLowerCase())
                    ? now
                    : [
                        ...now,
                        pick.kind === 'existing'
                          ? pick
                          : {
                              kind: 'typed',
                              label: pick.label,
                              target: pick.kind === 'typed' && pick.separate ? SEPARATE : null
                            }
                      ]
                )
              }
            />
            {plan.steps.some((step) => step.role === 'separate') && (
              <p role="note" className="text-sm text-amber-900">
                {SEPARATE_WARNING}
              </p>
            )}
          </fieldset>
          {!named && (!wires || others.length > 0) && <RouteChangeLine />}
          {held && (
            <p role="alert" className="text-sm text-destructive">
              {held}
            </p>
          )}
          {refused.form && (
            <p role="alert" className="text-sm text-destructive">
              {refused.form}
            </p>
          )}
          {said.length > 0 && (
            <ul role="status" className="grid gap-1 text-sm">
              {said.map((step) => (
                <li
                  key={step.text}
                  className={step.tone === 'refused' ? 'text-destructive' : 'text-foreground'}
                >
                  {step.tone === 'done' ? 'Done: ' : 'Refused: '}
                  {step.text}
                </li>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter className="items-center">
          {waiting >= 0 && (
            <p id={waitId} className="mr-auto text-sm text-muted-foreground">
              Choose which name “{others[waiting].label}” spells.
            </p>
          )}
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
            {made ? 'Close' : 'Cancel'}
          </Button>
          <Button
            type="submit"
            form={`${crdId}-form`}
            disabled={busy || crd.trim() === '' || waiting >= 0 || named}
            aria-describedby={named ? namedId : waiting >= 0 ? waitId : undefined}
          >
            {busy ? 'Adding…' : made ? 'Add the names left' : 'Add the firm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** An other name the form holds: a register name to map, or a typed one with its choice of what it
 *  spells — a register name's code, `SEPARATE`, or none yet. */
type IFormName =
  | { kind: 'existing'; code: string; label: string }
  | { kind: 'typed'; label: string; target: Nullable<string> };

/** The code that stands for the firm's first register name the form makes, before it exists. */
const FIRST = '@first';

/** What an other name of Add a firm does: a register name mapped; the firm's first register name;
 *  a spelling of one; a separate register name; or a spelling still waiting to be told which. */
type NameRole = 'existing' | 'first' | 'spelling' | 'separate' | 'choose';

interface IPlannedName {
  role: NameRole;
  /** The register names it could spell at its turn — several, and the reader chooses. */
  choices: ISpellingTarget[];
  target?: ISpellingTarget;
}

/**
 * Every other name's step, in order, from the register names the firm has before them (`start`).
 * With none, the first typed name is its first register name, and the others' choice after it. A
 * typed name with one choice spells it, unless asked for as separate; with several, it spells the
 * one chosen, or waits. `targets` is what a name typed next could spell.
 */
function planOf(
  others: readonly IFormName[],
  start: readonly ISpellingTarget[]
): { steps: IPlannedName[]; targets: ISpellingTarget[] } {
  const targets = [...start];
  const steps = others.map((other): IPlannedName => {
    if (other.kind === 'existing') return { role: 'existing', choices: [] };
    const choices = [...targets];
    if (choices.length === 0) {
      targets.push({ code: FIRST, label: other.label });
      return { role: 'first', choices };
    }
    const chosen = other.target ?? (choices.length === 1 ? choices[0].code : null);
    if (chosen === SEPARATE) return { role: 'separate', choices };
    const target = choices.find((choice) => choice.code === chosen);
    return target ? { role: 'spelling', choices, target } : { role: 'choose', choices };
  });
  return { steps, targets };
}

/** A chip's words for its other name's step. */
function roleWords(step: IPlannedName): string {
  switch (step.role) {
    case 'existing':
      return '';
    case 'first':
      return ' (new)';
    case 'separate':
      return ' (separate name)';
    case 'spelling':
      return step.choices.length > 1 && step.target ? ` (spelling of “${step.target.label}”)` : ' (spelling)';
    case 'choose':
      return ' (spelling)';
  }
}

/** A name, drawn as a chip, with its × where it can be taken away. */
export function NameChip({
  text,
  remove,
  disabled,
  describedBy,
  onRemove
}: {
  text: string;
  /** The ×'s accessible name; no × without it. */
  remove?: string;
  disabled?: boolean;
  describedBy?: string;
  onRemove?: () => void;
}) {
  return (
    <span className="inline-flex max-w-full items-center gap-0.5 rounded-full border border-border bg-muted/40 py-0.5 pr-1 pl-2.5 text-xs">
      <span className="min-w-0 break-words">{text}</span>
      {remove && onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-5 shrink-0 rounded-full text-muted-foreground"
          aria-label={remove}
          aria-describedby={describedBy}
          disabled={disabled}
          onClick={onRemove}
        >
          <X className="size-3" aria-hidden />
        </Button>
      ) : (
        <span className="w-1.5" />
      )}
    </span>
  );
}
